import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import apiClient from '../../api/client';
import {
  type ChatMessage,
  type GuidedSection,
  type MBCBReport,
  loadPersistedChat,
  persistChat,
} from '../../utils/mbcbReportShared';

interface AiAssistSheetProps {
  open: boolean;
  onClose: () => void;
  reportId: number;
  section: GuidedSection;
  canEdit: boolean;
  buildPayload: () => Record<string, unknown>;
  onReportUpdate: (report: MBCBReport) => void;
}

export const AiAssistSheet: React.FC<AiAssistSheetProps> = ({
  open,
  onClose,
  reportId,
  section,
  canEdit,
  buildPayload,
  onReportUpdate,
}) => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const startedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const restored = loadPersistedChat(reportId);
    setChatMessages(restored);
    startedRef.current = restored.length > 0 ? section : null;
  }, [open, reportId, section]);

  useEffect(() => {
    if (!open || !canEdit) return;
    if (startedRef.current === section) return;
    startedRef.current = section;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.post(`/mbcb/reports/${reportId}/ai/start`, { section });
        const data = res.data.data as {
          assistantMessage: string;
          report?: MBCBReport;
        };
        if (cancelled) return;
        setChatMessages((prev) => {
          const next = prev.length
            ? prev
            : [{ role: 'assistant' as const, content: data.assistantMessage || '' }];
          persistChat(reportId, next);
          return next;
        });
        if (data.report) onReportUpdate(data.report);
      } catch {
        if (!cancelled) {
          setChatMessages([
            {
              role: 'assistant',
              content:
                'I had trouble connecting. You can still fill in the fields directly — your work is saved as you go.',
            },
          ]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canEdit, reportId, section, onReportUpdate]);

  useEffect(() => {
    if (!open) return;
    persistChat(reportId, chatMessages);
  }, [open, reportId, chatMessages]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages, open]);

  const turnMutation = useMutation({
    mutationFn: async (messages: ChatMessage[]) => {
      await apiClient.put(`/mbcb/reports/${reportId}`, buildPayload());
      const res = await apiClient.post(`/mbcb/reports/${reportId}/ai/turn`, {
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        section,
      });
      return res.data.data as { assistantMessage: string; report?: MBCBReport };
    },
    onSuccess: (data) => {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.assistantMessage || '' },
      ]);
      if (data.report) onReportUpdate(data.report);
    },
  });

  const handleSend = () => {
    const t = chatInput.trim();
    if (!t || turnMutation.isPending || !canEdit) return;
    const userMsg: ChatMessage = { role: 'user', content: t };
    const next = [...chatMessages, userMsg];
    setChatMessages(next);
    setChatInput('');
    turnMutation.mutate(next);
  };

  if (!open) return null;

  const sectionLabel =
    section === 'people' ? 'People & story' : section === 'generosity' ? 'Generosity' : 'Final review';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 animate-fade-in"
        aria-label="Close assistant"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[85dvh] flex flex-col animate-slide-up pb-safe-bottom">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Talk it out</h3>
            <p className="text-xs text-gray-500">{sectionLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 min-h-touch min-w-touch"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div ref={chatScrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
          {chatMessages.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">Preparing a personalized prompt…</p>
          )}
          {chatMessages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : 'bg-white text-gray-900 border border-gray-100 shadow-sm rounded-bl-md'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {turnMutation.isPending && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 bg-white border-t border-gray-200 px-3 py-3">
          <div className="flex gap-2 items-end">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Share your thoughts…"
              disabled={!canEdit}
              className="flex-1 rounded-xl border border-gray-300 text-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-touch"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!canEdit || turnMutation.isPending || !chatInput.trim()}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-600 text-white disabled:opacity-40 shrink-0 min-h-touch min-w-touch"
              aria-label="Send"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
